import React, { createContext, useContext, useMemo, useRef } from 'react';
import type { MessageClass } from '@cotera/sdk/core';
import {
  buildCitationRegistry,
  createCitationExtractionCache,
} from './citation-extractors';
import { normalizeCitationUrl, type CitationSource } from './citation-source';

/** How a citation chip hands a click back to the host app. */
export type CitationClickHandler = (source: CitationSource) => void;

type CitationContextValue = {
  resolve: (href: string) => CitationSource | null;
  onCitationClick: CitationClickHandler | undefined;
};

const CitationContext = createContext<CitationContextValue | null>(null);

/**
 * Publishes the conversation's citable sources to the markdown renderer.
 *
 * Citation data must travel by context rather than by props: Streamdown's own
 * top-level memo compares only `children`/`shikiTheme`/`isAnimating`/`mode`, so
 * anything passed down through `components` closures goes stale on a settled
 * message. Context updates cross `React.memo` boundaries; props don't.
 */
export const CitationProvider: React.FC<{
  messages: readonly MessageClass[];
  onCitationClick?: CitationClickHandler;
  children: React.ReactNode;
}> = ({ messages, onCitationClick, children }) => {
  // Survives re-renders so a completed tool call is only ever extracted once,
  // even though `messages` gets a new identity on every stream tick.
  const cacheRef = useRef(createCitationExtractionCache());

  const registry = useMemo(
    () => buildCitationRegistry(messages, cacheRef.current),
    [messages]
  );

  const value = useMemo<CitationContextValue>(
    () => ({
      resolve: (href: string) => {
        const key = normalizeCitationUrl(href);
        if (key === undefined) {
          return null;
        }
        return registry.get(key) ?? null;
      },
      onCitationClick,
    }),
    [registry, onCitationClick]
  );

  return (
    <CitationContext.Provider value={value}>
      {children}
    </CitationContext.Provider>
  );
};

/** Stable identity so consumers outside a provider don't churn on every render. */
const NO_CITATIONS: CitationContextValue = {
  resolve: () => null,
  onCitationClick: undefined,
};

/**
 * Citation lookup for the markdown `a` override. Returns a null resolver when
 * no provider is mounted, so `Response` degrades to plain links outside chat.
 */
export function useCitations(): CitationContextValue {
  return useContext(CitationContext) ?? NO_CITATIONS;
}
